import { useEffect, useRef, useState, type ReactNode } from "react";
import { LazyMotion, MotionConfig, animate, domAnimation, m, useInView, type Transition } from "motion/react";
import { industry } from "../../content/industry";

/**
 * Animaciones de la plantilla sobre Motion (https://motion.dev). Cada estilo tiene una «personalidad» de movimiento:
 * elegante (lento y suave), tecnológico (rápido y preciso), juguetón (con rebote) o sereno (discreto).
 * Si el visitante pidió reducir el movimiento, MotionConfig desactiva desplazamientos y solo queda el fundido.
 */
type Personality = "elegant" | "tech" | "playful" | "calm";

const personalityByStyle: Record<string, Personality> = {
  "luxury-editorial": "elegant", "soft-luxury": "elegant", "architectural-minimal": "elegant", "dark-gastronomy": "elegant",
  "artisan-boutique": "elegant", "real-estate-premium": "elegant", "travel-immersive": "elegant",
  "dark-technology": "tech", "developer-blue": "tech", "modern-saas": "tech", "industrial-tech": "tech", "executive-corporate": "tech",
  "sweet-pastel": "playful", "groom-pop": "playful", "friendly-neighborhood": "playful",
  "premium-medical": "calm", "medical-minimal": "calm", "feline-calm": "calm", "emergency-pulse": "calm",
  "iron-volt": "tech", "chalk-box": "tech", "fight-poster": "tech", "neon-cardio": "playful", "zen-studio": "elegant",
};

const presets: Record<Personality, { hidden: Record<string, number | string>; transition: Transition }> = {
  elegant: { hidden: { opacity: 0, y: 28 }, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
  tech: { hidden: { opacity: 0, y: 20, scale: 0.985 }, transition: { duration: 0.55, ease: [0.16, 1, 0.3, 1] } },
  playful: { hidden: { opacity: 0, y: 32, scale: 0.97 }, transition: { type: "spring", bounce: 0.35, duration: 0.8 } },
  calm: { hidden: { opacity: 0, y: 18 }, transition: { duration: 0.7, ease: "easeOut" } },
};

/** Si el sitio usa un estilo de diseño distinto del original (looks.css), su movimiento manda sobre el de la plantilla. */
const personalityByLook: Record<string, Personality> = {
  minimal: "elegant", editorial: "elegant", neobrutalism: "playful", "hand-drawn": "playful", retro: "playful",
  constructivism: "tech", swiss: "tech", bento: "tech", flat: "calm",
};

const preset = presets[personalityByLook[industry.look ?? ""] ?? personalityByStyle[industry.style] ?? "calm"];
const shown = { opacity: 1, y: 0, scale: 1 };

/** Proveedor único: carga solo las funciones de animación del DOM (paquete ligero) y respeta «reducir movimiento». */
export function MotionProvider({ children }: { children: ReactNode }) {
  return (
    <LazyMotion features={domAnimation} strict>
      <MotionConfig reducedMotion="user">{children}</MotionConfig>
    </LazyMotion>
  );
}

/** Muestra su contenido con la animación del estilo cuando entra en pantalla (una sola vez). */
export function Reveal({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <m.div className={className} initial={preset.hidden} whileInView={shown} viewport={{ once: true, amount: 0.12 }} transition={preset.transition}>
      {children}
    </m.div>
  );
}

/**
 * Cifra que cuenta desde cero al verse («+25 años», «98 %», «4.9»). El texto final está siempre en el HTML; si el valor
 * no empieza con un número (por ejemplo «24/7») se muestra tal cual.
 */
const numberPattern = /^(\D*?)(\d+(?:[.,]\d+)?)(.*)$/;
const reducedMotion = () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** Escribe «current» con los mismos decimales, separador, prefijo y sufijo que el valor original. */
function formatLike(value: string, current: number) {
  const match = numberPattern.exec(value);
  if (!match) return value;
  const [, prefix, number, suffix] = match;
  const decimals = number.split(/[.,]/)[1]?.length ?? 0;
  return prefix + current.toFixed(decimals).replace(".", number.includes(",") ? "," : ".") + suffix;
}

export function CountUp({ value }: { value: string }) {
  const animated = numberPattern.test(value) && !reducedMotion();
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.6 });
  const [text, setText] = useState(() => (animated ? formatLike(value, 0) : value));

  useEffect(() => {
    if (!animated || !inView) return;
    const target = Number(numberPattern.exec(value)![2].replace(",", "."));
    const controls = animate(0, target, {
      duration: 1.6,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (current) => setText(formatLike(value, current)),
    });
    return () => controls.stop();
  }, [animated, inView, value]);

  return (
    <span ref={ref}>
      <span aria-hidden="true">{text}</span>
      <span className="ix-sr-only">{value}</span>
    </span>
  );
}
